import { useFetcher, useNavigation, useParams } from "react-router-dom";

import Button from "components/Button";
import CouponCodeInput from "v2/components/billing/CouponCodeInput";
import SubmitButton from "components/SubmitButton";
import TaggedText from "components/TaggedText";

import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";
import { useDiscountPrice } from "v2/hooks/billing/useDiscountPrice";
import { useAllPlans } from "routes/data-routes/billing-data/plans";

import { useUser } from "routes/dataroutes/UserData";

import Modal, {
  ModalButtonsSection,
  ModalContent,
  ModalSections
} from "./index";

import { fmtCurrency, fmtDate } from "utils";
import getDataFromSuccessfulResponse from "utils/getDataFromSuccessfulResponse";
import { isLegacyAnnualPlan } from "utils/planUtils";

import { Coupon } from "types/Coupons";
import { CheckCouponsFetcherDataType } from "routes/data-routes/billing-data/coupons/check-coupons/route.meta";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { ActiveSubscriptionFormActions } from "routes/data-routes/billing-data/active-subscription/ActiveSubscriptionFormActions";
import {
  ActiveSubscriptionDataRoutePath
} from "routes/data-routes/billing-data/active-subscription/route.meta";


export default function OldUpgradeSubscriptionModal() {
  const { planId } = useParams() as { planId: string };

  const navigation = useNavigation();

  const user = useUser();
  const { activeSubscription, activeSubscriptionFetcher } = useActiveSubscription();
  const { allPlans } = useAllPlans();
  const { activeCoupons } = useActiveCoupons();
  const couponFetcher = useFetcher<CheckCouponsFetcherDataType>();

  const targetPlan = allPlans?.flat()?.find(p => p.planSlug === planId);
  const discountPrice = useDiscountPrice({
    useScheduledSubscriptionIfAny: false,  // not checking here if other plans have already been scheduled or not... if one upgrades manually, they should always get their target plans
    coupon: getDataFromSuccessfulResponse<Coupon>(couponFetcher.data),
    targetPlan: targetPlan
  });
  const isOnAnnualPlan = isLegacyAnnualPlan(activeSubscription?.planSlug);


  // <CouponCodeInput> will add the target plan id as a hidden input (when visible)
  return (
    <Modal
      headline="Upgrade Subscription"
      preventClosingIf={ navigation.state !== "idle" }
    >
      { ({ closeModal }) =>
        <activeSubscriptionFetcher.Form method="POST" action={ ActiveSubscriptionDataRoutePath }>
          <ModalContent>
            <ModalSections>
              <div>
                Are you sure you want to upgrade your subscription to <span className="font-bold capitalize">{ targetPlan?.planName }</span>?
              </div>

              { isOnAnnualPlan && (
                <div>
                  By upgrading your subscription, we will reset your credit counters and
                  invoice <span className="font-bold">{ fmtCurrency(discountPrice / 100) }</span> on { fmtDate(new Date((activeSubscription?.currentTermEnd || 0) * 1000)) },
                  when your annual subscription ends.
                  <TaggedText
                    message=" If you would like to end your annual subscription and upgrade earlier, please [contact our support team|contact_support]."
                    tagCallbacks={ { contact_support: closeModal } }
                  />
                </div>
              ) }

              { !isOnAnnualPlan && (
                <>
                  <div>
                    By upgrading your subscription, we will reset your credit counters,
                    invoice <span className="font-bold">{ fmtCurrency(discountPrice / 100) }</span> now and set your
                    billing date to today.
                  </div>
                  {/* TODO it is hardcoded here that we don't allow coupons for annual subscriptions. should we change it and let Chargebee make that decision? */ }
                  { (user?.canUseAllCoupons || (user?.canUseCoupons && !isLegacyAnnualPlan(planId))) && (
                    <CouponCodeInput
                      fetcher={ couponFetcher }
                    />
                  ) }
                  { ((user?.canUseAllCoupons === false) && isLegacyAnnualPlan(planId) && ((activeCoupons?.length || 0) > 0)) && (
                    <div>
                      Your current extra discount coupons will also be removed after the upgrade.
                    </div>
                  ) }
                </>
              ) }

            </ModalSections>
            <ModalButtonsSection>
              <Button text="Cancel" className="button button-secondary" onClick={ closeModal } size="MD"/>

              { isOnAnnualPlan && (
                <>
                  <SubmitButton
                    text="Upgrade at End of Cycle"
                    className="button button-primary"
                    size="MD"
                    checkFormValidity
                    { ...ActiveSubscriptionFormActions.upgrade }
                  />
                </>
              ) }

              { !isOnAnnualPlan && (
                <SubmitButton
                  text="Upgrade Subscription"
                  className="button button-primary"
                  size="MD"
                  checkFormValidity
                  { ...ActiveSubscriptionFormActions.upgrade }
                />
              ) }
            </ModalButtonsSection>
          </ModalContent>
        </activeSubscriptionFetcher.Form>
      }
    </Modal>
  );
};
